import { create, type StoreApi, type UseBoundStore } from "zustand";
import { persist } from "zustand/middleware";

export interface ViewedItem {
  /** 新APIではスレッドIDが ULID(string) になったため string で保持する */
  id: string;
  ts: number;
  /** どの板のスレッドか。board routing 導入 (v2) で追加。
   * v1 以前のデータは migrate で空文字列にされる → 表示時 fallback で現在の板に紐づける */
  boardSlug: string;
}

interface HistoryState {
  viewed: ViewedItem[];
  addViewed: (threadId: string, boardSlug: string) => void;
  removeFromHistory: (threadId: string) => void;
  clearHistory: () => void;
  getViewedIds: () => string[];
  /** boardSlug 込みで全エントリを返す。`useHistoryThreads` 用。 */
  getViewedItems: () => ViewedItem[];
}

const MAX_VIEWED = 100;

export const useHistoryStore: UseBoundStore<StoreApi<HistoryState>> =
  create<HistoryState>()(
    persist(
      (set, get) => ({
        viewed: [],

        addViewed(threadId: string, boardSlug: string): void {
          set((state) => {
            const filtered = state.viewed.filter((v) => v.id !== threadId);
            const newViewed = [
              { id: threadId, ts: Date.now(), boardSlug },
              ...filtered,
            ];
            return { viewed: newViewed.slice(0, MAX_VIEWED) };
          });
        },

        removeFromHistory(threadId: string): void {
          set((state) => ({
            viewed: state.viewed.filter((v) => v.id !== threadId),
          }));
        },

        clearHistory: () => set({ viewed: [] }),

        getViewedIds: () => get().viewed.map((v) => v.id),

        getViewedItems: () => get().viewed,
      }),
      {
        name: "aimg-history",
        version: 2,
        // version 1: 旧 API では threadId が number だったため localStorage に
        //   数値 id が残っているケースがある。ULID(string) への移行に合わせて
        //   String() で正規化する。
        // version 2: board routing 導入で ViewedItem に boardSlug を追加。
        //   v1 以前のエントリは boardSlug を空文字で持たせ、表示時に
        //   現在の board へ fallback する。
        migrate: (persisted: unknown, version: number): HistoryState => {
          if (!persisted || typeof persisted !== "object") {
            return { viewed: [] } as unknown as HistoryState;
          }
          const state = persisted as HistoryState;
          let viewed = Array.isArray(state.viewed) ? state.viewed : [];
          if (version < 1) {
            viewed = viewed.map((v) => ({ ...v, id: String(v.id) }));
          }
          if (version < 2) {
            viewed = viewed.map((v) => ({
              ...v,
              boardSlug: typeof v.boardSlug === "string" ? v.boardSlug : "",
            }));
          }
          return { ...state, viewed } as HistoryState;
        },
      },
    ),
  );
